// Tiny data-fetching primitive. Not as much as react-query gives, but
// react-query is a 14 KB dep for v0 needs that boil down to "GET this
// path once on mount, expose {data, error, loading, refetch}." Stay
// scrappy until we actually have UI complex enough to need cache
// invalidation across components.
//
// Usage:
//   const { data, error, loading, refetch } = useFetch(async (api) =>
//     api.GET("/sandboxes", { params: { query: { limit: 50 } } }),
//   );
import { useCallback, useEffect, useState } from "react";
import { useApi, describeError } from "./client";

type FetchResult<T> = {
  data: T | null;
  error: string | null;
  loading: boolean;
  refetch: () => void;
};

type ApiClient = ReturnType<typeof useApi>;

export function useFetch<T>(
  call: (api: ApiClient) => Promise<{ data?: T; error?: unknown; response: Response }>,
  deps: unknown[] = [],
): FetchResult<T> {
  const api = useApi();
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [bump, setBump] = useState(0);

  const refetch = useCallback(() => setBump((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    call(api).then(
      (res) => {
        if (cancelled) return;
        if (res.error) {
          setError(describeError(res.error, res.response.status));
          setData(null);
        } else {
          setData((res.data ?? null) as T | null);
        }
        setLoading(false);
      },
      (err) => {
        if (cancelled) return;
        setError(String(err));
        setData(null);
        setLoading(false);
      },
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, bump]);

  return { data, error, loading, refetch };
}
